import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Collapse, Link, Stack, Typography, alpha } from "@mui/material";
import { HistoryRounded as HistoryIcon } from "@mui/icons-material";
import { useGetList } from "react-admin";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { featureFlags } from "@/utils/featureFlags";
import { claimItemsByReasonPath } from "@/resources/stoploss-claims/claimItems/ineligibleReasons";
import { DocumentLoadStatus, ResourceName, type DocumentLoad, type DocumentRead, type Policy } from "@/types";
import { SectionCard } from "@/components/common/SectionCard";
import { HELD_REASON_TEXT, parseLoadWarnings } from "./parseLoadWarnings";
import { LoadWarningNotice } from "./LoadWarningNotice";
import { buildAttentionMap } from "./loadResultModel";
import { FailedRecordsAccordion } from "./FailedRecordsAccordion";
import { ResultContextBar } from "./ResultContextBar";
import { LoadClaimantsTable } from "./LoadClaimantsTable";
import { useLoadResultData } from "./useLoadResultData";
import { STATUS_CONFIG } from "./config";

interface LoadResultViewProps {
  documentRecord: DocumentRead;
  latestLoad: DocumentLoad;
  previousLoadsCount: number;
  previousLoadsOpen?: boolean;
  onTogglePreviousLoads?: () => void;
}

/**
 * Post-load Result for a single-client claims document (#7550): the read-only
 * context bar, an exception-only status strip (Partial / Failed), load
 * warnings, and the claimants table. The success band, the stat cards and the
 * "Load Successful · N records loaded" strip are gone; the header chip already
 * says Completed and the table carries the numbers.
 */
export const LoadResultView = ({
  documentRecord,
  latestLoad,
  previousLoadsCount,
  previousLoadsOpen,
  onTogglePreviousLoads,
}: LoadResultViewProps) => {
  const clientId = documentRecord.client_id_ref;
  const { data: policies } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 10 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId, staleTime: 60_000 }
  );
  // The load's warnings land on the claimant rows they are about (held spend,
  // skipped totals) plus one quiet line above the table — no page-wide banner.
  const parsedWarnings = useMemo(() => parseLoadWarnings(latestLoad.normalization_warnings), [latestLoad]);
  const attention = useMemo(() => buildAttentionMap(parsedWarnings, HELD_REASON_TEXT), [parsedWarnings]);
  const { rows, isLoading, linesPartial, policy } = useLoadResultData(
    latestLoad,
    policies ?? [],
    attention,
    String(documentRecord.id)
  );
  const { canList: canListClaimLines } = useResourcePermissions(ResourceName.CLAIM_ITEMS);
  // Flag off → the legacy stamp never writes missing_paid_date: no link, no facet hint.
  const reasonFacetAvailable = featureFlags.deductibleBasisViewEnabled;
  const notice = (
    <LoadWarningNotice
      parsed={parsedWarnings}
      reasonFacetAvailable={reasonFacetAvailable}
      noPaidDateHref={
        canListClaimLines && reasonFacetAvailable
          ? claimItemsByReasonPath(["missing_paid_date"], clientId ? { client_id_ref: clientId } : {})
          : null
      }
    />
  );

  return (
    <Box data-testid="load-result-view">
      <ResultContextBar documentRecord={documentRecord} policy={policy} />
      <ExceptionStrip load={latestLoad} />
      {previousLoadsCount > 0 && onTogglePreviousLoads && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={onTogglePreviousLoads}
            size="small"
            variant="text"
            sx={{ textTransform: "none" }}
          >
            {previousLoadsOpen ? "Hide" : "Show"} previous loads ({previousLoadsCount})
          </Button>
        </Box>
      )}
      <SectionCard noPadding compact>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ pt: 1.5 }}>
            <LoadClaimantsTable rows={rows} linesPartial={linesPartial} notice={notice} />
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

/**
 * Renders only for a Partial or Failed load (#7550 AC8): "<status> · N rows
 * didn't load · Show", with Show revealing the failed rows. A clean load has
 * no strip; the page says "done" once, in the header chip.
 */
export const ExceptionStrip = ({ load }: { load: DocumentLoad }) => {
  const [open, setOpen] = useState(false);
  if (load.status === DocumentLoadStatus.SUCCESS) return null;
  const config = STATUS_CONFIG[load.status];
  const Icon = config.icon;
  const failed = load.failed_records?.length ?? 0;
  const label = load.status === DocumentLoadStatus.FAILED ? "Load failed" : "Partial load";
  return (
    <Box sx={{ mb: 2 }} data-testid="load-exception-strip">
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          border: 1,
          borderColor: (theme) => alpha(theme.palette[config.color].main, 0.4),
          borderLeft: 4,
          borderLeftColor: `${config.color}.main`,
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette[config.color].main, theme.palette.mode === "dark" ? 0.1 : 0.06),
        }}
      >
        <Icon sx={{ fontSize: 18, color: `${config.color}.main` }} />
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          · {failed} {failed === 1 ? "row" : "rows"} didn't load
        </Typography>
        {failed > 0 && (
          <Link component="button" type="button" variant="body2" onClick={() => setOpen((v) => !v)} sx={{ ml: "auto" }}>
            {open ? "Hide" : "Show"}
          </Link>
        )}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 1 }}>
          <FailedRecordsAccordion failedRecords={load.failed_records ?? []} schemaLabel="Rows" />
        </Box>
      </Collapse>
    </Box>
  );
};
